import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { getPractice, fetchPracticeTemplates } from "../../api";

const URL =
  process.env.REACT_APP_API_BASE_PROD_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:3002";

const quillModules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
  ],
};
const quillFormats = ["bold", "italic", "underline", "list", "bullet", "align", "link"];

function formatDate(date) {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-ZA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function ViewIntakeSettings({ refresh }) {
  const [practices, setPractices] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [practice, setPractice] = useState(null);
  const [editing, setEditing] = useState(false);
  const [termsHtml, setTermsHtml] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Intake templates UI state
  const [allIntakeTemplates, setAllIntakeTemplates] = useState([]); // [{ _id, templateTitle, templatePracticeId }]
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]); // ordered string ids
  const [editingTemplates, setEditingTemplates] = useState(false);
  const [savingTemplates, setSavingTemplates] = useState(false);
  const [savingWhatsAppQuestion, setSavingWhatsAppQuestion] = useState(false);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const token = sessionStorage.getItem("user");
      if (!token) { setLoading(false); return; }
      const decoded = jwtDecode(token);
      const [allPrac, allTpls] = await Promise.all([
        getPractice(),
        fetchPracticeTemplates(decoded._id),
      ]);
      const filtered = (allPrac || []).filter((p) =>
        (decoded.practice || []).includes(p.practiceNumber)
      ).sort((a, b) => {
        if (a.holdingPractice === "true" && b.holdingPractice !== "true") return -1;
        if (a.holdingPractice !== "true" && b.holdingPractice === "true") return 1;
        return (a.practiceName || "").localeCompare(b.practiceName || "");
      });
      setPractices(filtered);
      setAllIntakeTemplates(
        (allTpls || []).filter((t) =>
          t.templateInitial === "intake" && t.templateStatus === "Active"
        )
      );
      if (filtered.length > 0) {
        const first = filtered[0];
        setSelectedId(first._id);
        setPractice(first);
        setSelectedTemplateIds(first.intakeSettings?.intakeTemplateIds || []);
      }
      setLoading(false);
    }
    load();
  }, [refresh]);

  function handlePracticeSelect(id) {
    setSelectedId(id);
    setEditing(false);
    setEditingTemplates(false);
    const found = practices.find((p) => p._id === id);
    setPractice(found || null);
    setSelectedTemplateIds(found?.intakeSettings?.intakeTemplateIds || []);
  }

  function buildAuditMeta() {
    const token = sessionStorage.getItem("user");
    const decoded = token ? jwtDecode(token) : {};
    return {
      updatedAt: new Date().toISOString(),
      updatedBy: `${decoded.firstName || ""} ${decoded.lastName || ""}`.trim() || decoded.email || "",
    };
  }

  async function saveIntakeSettings(partial) {
    // Merges `partial` over the practice's existing intakeSettings so each save
    // preserves fields it doesn't touch (e.g. saving templates keeps T&C, and vice versa).
    const merged = {
      ...(practice?.intakeSettings || {}),
      ...partial,
      ...buildAuditMeta(),
    };
    await axios.put(`${URL}/practice/${practice._id}`, { intakeSettings: merged });
    setPractice((prev) => ({ ...prev, intakeSettings: merged }));
    return merged;
  }

  async function handleSave() {
    if (!practice) return;
    setSaving(true);
    try {
      await saveIntakeSettings({ termsAndConditions: termsHtml });
      setEditing(false);
    } catch (err) {
      console.error("Failed to save intake settings:", err);
    } finally {
      setSaving(false);
    }
  }

  // The question is only ever SHOWN when the practice has WhatsApp connected
  // (the server decides that when it creates the intake session), so this switch
  // is for a practice that has WhatsApp but does not want intake asking.
  async function handleToggleWhatsAppQuestion(next) {
    if (!practice) return;
    setSavingWhatsAppQuestion(true);
    try {
      await saveIntakeSettings({ whatsappConsentQuestion: next });
    } catch (err) {
      console.error("Failed to save WhatsApp consent question setting:", err);
    } finally {
      setSavingWhatsAppQuestion(false);
    }
  }

  async function handleSaveTemplates() {
    if (!practice) return;
    setSavingTemplates(true);
    try {
      await saveIntakeSettings({ intakeTemplateIds: selectedTemplateIds });
      setEditingTemplates(false);
    } catch (err) {
      console.error("Failed to save intake templates:", err);
    } finally {
      setSavingTemplates(false);
    }
  }

  // Helpers for the ordered selection
  function moveSelected(index, delta) {
    setSelectedTemplateIds((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }
  function removeSelected(id) {
    setSelectedTemplateIds((prev) => prev.filter((t) => t !== id));
  }
  function addSelected(id) {
    setSelectedTemplateIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }

  const availableForPractice = allIntakeTemplates.filter((t) =>
    Array.isArray(t.templatePracticeId) &&
    practice && t.templatePracticeId.includes(practice.practiceNumber)
  );
  const availableUnselected = availableForPractice.filter(
    (t) => !selectedTemplateIds.includes(String(t._id))
  );
  const selectedResolved = selectedTemplateIds
    .map((id) => availableForPractice.find((t) => String(t._id) === id))
    .filter(Boolean);

  if (loading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading…
      </Container>
    );
  }

  return (
    <Container className="py-3">
      <Row className="mb-3">
        <Col md={4}>
          <Form.Label className="fw-semibold">Practice</Form.Label>
          <Form.Select
            value={selectedId}
            onChange={(e) => handlePracticeSelect(e.target.value)}
            disabled={practices.length <= 1}
          >
            {practices.map((p) => (
              <option key={p._id} value={p._id}>
                {p.practiceName || p.practiceNumber}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Intake settings are configured per practice.
          </Form.Text>
        </Col>
      </Row>

      {practice && (
        <div className="bg-white rounded shadow-sm p-4 mb-3">
          <h6 className="fw-semibold mb-3 text-primary">
            Terms &amp; Conditions — {practice.practiceName || practice.practiceNumber}
          </h6>

          {!editing ? (
            practice.intakeSettings?.termsAndConditions ? (
              <>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="badge bg-success">Active</span>
                  {practice.intakeSettings.updatedAt && (
                    <small className="text-muted">
                      Last updated: {formatDate(practice.intakeSettings.updatedAt)}
                      {practice.intakeSettings.updatedBy ? ` by ${practice.intakeSettings.updatedBy}` : ""}
                    </small>
                  )}
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="ms-auto"
                    onClick={() => {
                      setTermsHtml(practice.intakeSettings.termsAndConditions);
                      setEditing(true);
                    }}
                  >
                    Edit
                  </Button>
                </div>
                <div
                  className="border rounded p-3 bg-light small"
                  style={{ maxHeight: 360, overflowY: "auto" }}
                  dangerouslySetInnerHTML={{ __html: practice.intakeSettings.termsAndConditions }}
                />
              </>
            ) : (
              <Alert variant="warning" className="d-flex align-items-center gap-3">
                <div>
                  <strong>No terms and conditions configured.</strong> The intake send button will not
                  appear on appointments until you add terms and conditions for this practice.
                </div>
                <Button
                  size="sm"
                  variant="warning"
                  className="ms-auto flex-shrink-0"
                  onClick={() => { setTermsHtml(""); setEditing(true); }}
                >
                  Set Up
                </Button>
              </Alert>
            )
          ) : (
            <>
              <ReactQuill
                theme="snow"
                value={termsHtml}
                onChange={setTermsHtml}
                modules={quillModules}
                formats={quillFormats}
                style={{ minHeight: 200 }}
              />
              <div className="d-flex gap-2 mt-3">
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Saving…
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
                <Button variant="outline-secondary" onClick={() => setEditing(false)} disabled={saving}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {practice && (
        <div className="bg-white rounded shadow-sm p-4 mb-3">
          <h6 className="fw-semibold mb-3 text-primary">
            WhatsApp Consent — {practice.practiceName || practice.practiceNumber}
          </h6>
          <div className="d-flex align-items-center gap-2">
            <Form.Check
              type="switch"
              id="whatsappConsentQuestion"
              label="Ask patients on the intake form whether they may be contacted on WhatsApp"
              checked={practice.intakeSettings?.whatsappConsentQuestion !== false}
              disabled={savingWhatsAppQuestion}
              onChange={(e) => handleToggleWhatsAppQuestion(e.target.checked)}
            />
            {savingWhatsAppQuestion && <Spinner size="sm" animation="border" />}
          </div>
          <Form.Text className="text-muted d-block mt-2">
            The question only appears once this practice has connected a WhatsApp
            number. Patients who agree can still stop the messages at any time by
            replying STOP, and consent can also be recorded on the patient record.
          </Form.Text>
        </div>
      )}

      {practice && (
        <div className="bg-white rounded shadow-sm p-4">
          <div className="d-flex align-items-center mb-3">
            <h6 className="fw-semibold text-primary mb-0">
              Intake Templates — {practice.practiceName || practice.practiceNumber}
            </h6>
            {!editingTemplates && (
              <Button
                size="sm"
                variant="outline-primary"
                className="ms-auto"
                onClick={() => {
                  setSelectedTemplateIds(practice.intakeSettings?.intakeTemplateIds || []);
                  setEditingTemplates(true);
                }}
              >
                Edit
              </Button>
            )}
          </div>
          <Form.Text className="text-muted d-block mb-3">
            Each page of each selected template becomes a step in the patient's intake form,
            inserted before the Terms &amp; Conditions step. Only Active templates with category
            "Intake" appear here.
          </Form.Text>

          {!editingTemplates ? (
            selectedResolved.length === 0 ? (
              <Alert variant="light" className="mb-0 border">
                <small className="text-muted">
                  No intake templates attached. Patients will see the standard intake form only.
                </small>
              </Alert>
            ) : (
              <ol className="mb-0 ps-3">
                {selectedResolved.map((t) => (
                  <li key={String(t._id)} className="py-1">
                    {t.templateTitle}
                  </li>
                ))}
              </ol>
            )
          ) : (
            <>
              <Row>
                <Col md={6}>
                  <div className="fw-semibold small mb-2">Available</div>
                  <div className="border rounded" style={{ maxHeight: 320, overflowY: "auto" }}>
                    {availableUnselected.length === 0 ? (
                      <div className="text-muted small p-3">
                        No additional intake templates available for this practice.
                      </div>
                    ) : (
                      availableUnselected.map((t) => (
                        <div
                          key={String(t._id)}
                          className="d-flex align-items-center px-3 py-2 border-bottom"
                        >
                          <span className="flex-grow-1">{t.templateTitle}</span>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => addSelected(String(t._id))}
                          >
                            Add
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="fw-semibold small mb-2">Selected (in step order)</div>
                  <div className="border rounded" style={{ maxHeight: 320, overflowY: "auto" }}>
                    {selectedResolved.length === 0 ? (
                      <div className="text-muted small p-3">
                        None selected. Add templates from the left to include them in the intake form.
                      </div>
                    ) : (
                      selectedResolved.map((t, idx) => (
                        <div
                          key={String(t._id)}
                          className="d-flex align-items-center px-3 py-2 border-bottom"
                        >
                          <span className="me-2 text-muted small">{idx + 1}.</span>
                          <span className="flex-grow-1">{t.templateTitle}</span>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            className="me-1"
                            disabled={idx === 0}
                            onClick={() => moveSelected(idx, -1)}
                            title="Move up"
                          >
                            ↑
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            className="me-1"
                            disabled={idx === selectedResolved.length - 1}
                            onClick={() => moveSelected(idx, 1)}
                            title="Move down"
                          >
                            ↓
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => removeSelected(String(t._id))}
                            title="Remove"
                          >
                            ×
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </Col>
              </Row>
              <div className="d-flex gap-2 mt-3">
                <Button variant="primary" onClick={handleSaveTemplates} disabled={savingTemplates}>
                  {savingTemplates ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Saving…
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setSelectedTemplateIds(practice.intakeSettings?.intakeTemplateIds || []);
                    setEditingTemplates(false);
                  }}
                  disabled={savingTemplates}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Container>
  );
}
